import { tracked } from '@glimmer/tracking';

import type { Theme } from 'webapp/enums/enums';

import type { FilterIdType } from 'webapp/models-custom/filter-sort/base';
import type PopupContext from 'webapp/models-custom/popup/context';
import type StatusObject from 'webapp/models-custom/status-object';

export const defaultFilterValues = [0, 'all'];
export const RECENTLY_DELETED_ID = 99;

type GetOptionsFn<IdType extends FilterIdType> = () => FilterOption<IdType>[];
type FilterFn<ItemType, IdType extends FilterIdType> = (
	item: ItemType,
	selected: IdType[],
) => boolean;
type OnSelectedChangeFn<ItemType, IdType extends FilterIdType> = (
	filter: Filter<ItemType, IdType>,
) => void;
type CreatePopupFn = (
	attachTo: HTMLElement,
	extraClass: string,
	theme?: Theme,
) => PopupContext | undefined;

export interface FilterSettings<ItemType, IdType extends FilterIdType> {
	id: number; // matches with the one in user settings in browser, not related to order
	label: string; // shown next to selector
	name: string; // shown when showing noResults text
	getOptions: GetOptionsFn<IdType>;
	filterFn?: FilterFn<ItemType, IdType>;
	onSelectedChange: OnSelectedChangeFn<ItemType, IdType>;
	isSelectDropdown?: boolean;
	isSort?: boolean;
	selected?: IdType | IdType[];
	createPopupFn?: CreatePopupFn;
	manualHide?: boolean;
	stringValues?: boolean;
	extraClass?: string;
	searchableProperties?: string[];
	isMultiSelect?: boolean;
	defaultValue?: IdType;
}

export interface FilterOption<IdType> {
	id: IdType;
	text: string;
	id2?: number | string;
	text2?: string;
	used?: number; // badge after text that shows number
	img?: string; // image to be shown in front of item text - if image is set as empty text, text initials will be shown instead
	color?: string; // color of icon (e.g. tags)
	icon?: IconName;
	class?: string; // extra class
	isDisabled?: boolean;
	statusObject?: StatusObject;
	affixIcon?: IconName;
	text3?: string; // used for connection name in source filter
	notCombinable?: boolean; // when true, this option cannot be combined with others (like default options)
	allowDuplicateSelection?: boolean;
}

export default class Filter<ItemType, IdType extends FilterIdType> {
	@tracked id;
	@tracked label;
	@tracked name;
	@tracked manualHide = false;
	@tracked isSelectDropdown = false;
	@tracked customCreatePopupFn: CreatePopupFn | null = null;
	@tracked isRecentlyDeletedSelected = false;
	@tracked isMultiSelect = false;

	// Sorting filter does not require filterFn, consequently, we had to make it optional.
	// (e.g. Data Manager -> Available Integrations -> Sort by Popularity/Alphabetically)
	@tracked isSort = false;

	getOptions: GetOptionsFn<IdType>;
	filterFn?: FilterFn<ItemType, IdType>;
	onSelectedChange: OnSelectedChangeFn<ItemType, IdType>;

	stringValues = false;
	extraClass = '';
	private readonly customDefaultValue?: IdType;

	searchableProperties;

	constructor(options: FilterSettings<ItemType, IdType>) {
		this.id = options.id;
		this.label = options.label;
		this.name = options.name;

		// functions for live data
		this.getOptions = options.getOptions;
		this.filterFn = options.filterFn;
		this.onSelectedChange = options.onSelectedChange;

		// optional flags and values
		if (options.createPopupFn) {
			this.customCreatePopupFn = options.createPopupFn;
		}
		if (options.manualHide) {
			this.manualHide = !!options.manualHide;
		}
		if (options.stringValues) {
			this.stringValues = !!options.stringValues;
		}
		if (options.isSelectDropdown) {
			this.isSelectDropdown = !!options.isSelectDropdown;
		}
		if (options.isSort) {
			this.isSort = !!options.isSort;
		}
		if (options.searchableProperties) {
			this.searchableProperties = options.searchableProperties;
		}
		if (options.defaultValue !== undefined) {
			this.customDefaultValue = options.defaultValue;
		}

		if (options.selected) {
			// ensure selected is always an array, whether it's a single value or array
			let selected = Array.isArray(options.selected) ? options.selected : [options.selected];

			// this handles cases where URL hash values are parsed as numbers but filter options use string ids
			if (this.stringValues) {
				selected = selected.map((value) =>
					typeof value === 'number' ? (String(value) as IdType) : value,
				);
			}

			this._selected = selected;
		} else if (
			!this.options.length ||
			this.options.find((opt) => opt.id === this.defaultValue)
		) {
			// there are some filters that do not have the "all" value, f.ex. client goal daterange
			this._selected = [this.defaultValue];
		} else {
			this._selected = this.options[0]?.id ? [this.options[0].id] : [];
		}

		if (options.isMultiSelect) {
			this.isMultiSelect = !!options.isMultiSelect;
		}

		this.extraClass = options.extraClass || '';
	}

	@tracked _selected: IdType[];

	get selected(): IdType[] {
		return this._selected;
	}

	get isNumeric(): boolean {
		return !this.stringValues;
	}

	get optionsForDropdown(): FilterOption<IdType>[] | { value: FilterIdType; label: string }[] {
		// for modal wizards goals / metrics
		let options = this.getOptions();
		if (this.isSelectDropdown) {
			options = options.map((opt) => {
				return Object.assign(
					{
						value: opt.id,
						label: opt.text,
					},
					opt,
				);
			});
		}
		return options;
	}

	get options(): FilterOption<IdType>[] {
		return this.getOptions();
	}

	get defaultValue(): IdType {
		if (this.customDefaultValue !== undefined) {
			return this.customDefaultValue;
		}
		return (this.stringValues ? 'all' : 0) as IdType;
	}

	get selectedData(): FilterOption<IdType>[] | null {
		if (this.isMultiSelect) {
			const filterOptions: FilterOption<IdType>[] = [];
			for (const value of this.selected) {
				const option = this.options.find((opt) => opt.id === value);
				if (option) {
					filterOptions.push(option);
				}
			}
			return filterOptions;
		} else {
			// for single select, return the first (and only) selected option as an array
			if (this.selected.length > 0) {
				const option = this.options.find((opt) => opt.id === this.selected[0]);
				return option ? [option] : [];
			}
			return [];
		}
	}

	get selectedDisplayText(): string {
		if (this.selected.length === 0) {
			return '';
		}

		if (this.selected.length === 1) {
			const option = this.options.find((opt) => opt.id === this.selected[0]);
			return option ? option.text : '';
		} else {
			return `${this.selected.length} filters`;
		}
	}

	get isShown(): boolean {
		if (this.manualHide) {
			return false;
		} else {
			// always show tag filter. others - only if we have values
			return this.isTag || this.options.length > 0;
		}
	}

	get isDefaultSelected(): boolean {
		if (this.customDefaultValue !== undefined) {
			return this.selected.length === 1 && this.selected[0] === this.customDefaultValue;
		}
		// if default is selected, nothing else can be selected anyway (check handleMultiSelect)
		return defaultFilterValues.includes(this.selected[0]!);
	}

	get hasRecentlyDeletedOption(): boolean {
		return this.options.some((option) => option.id === RECENTLY_DELETED_ID);
	}

	get isActive(): boolean {
		return this.isShown && (this.hasRecentlyDeletedOption || !this.isDefaultSelected);
	}

	get isTag(): boolean {
		// exception
		return this.name === 'tag';
	}

	get shouldShowClearSelection(): boolean {
		return this.isMultiSelect && this.selected.length > 1;
	}

	checkSelected(): void {
		if (this.isShown && (!this.selectedData || this.selectedData.length === 0)) {
			const fallbackValue = this.options[0]?.id || this.defaultValue;
			this.selectOption(fallbackValue);
		}
	}

	selectOption(clickedId: IdType | IdType[]): void {
		if (Array.isArray(clickedId)) {
			const validIds = clickedId.filter(this.isValidId);
			if (validIds.length > 0) {
				this._selected = validIds;
			}
		} else if (this.isValidId(clickedId)) {
			if (this.isMultiSelect) {
				this.handleMultiSelect(clickedId);
			} else {
				this._selected = [clickedId];
			}
		}
		this.onSelectedChange(this);
	}

	shouldClosePopupOnSelection(clickedId: IdType): boolean {
		// Always close for single-select filters
		if (!this.isMultiSelect) {
			return true;
		}

		// check if the clicked option is not combinable
		return this.isNotCombinable(clickedId);
	}

	use(items: ItemType[]): ItemType[] {
		const filterFn = this.filterFn;

		if (!filterFn) {
			return items;
		}

		return items.filter((item) => {
			return filterFn(item, this.selected);
		});
	}

	private isValidId = (id: IdType): boolean => {
		return id !== null && id !== undefined;
	};

	private isNotCombinable(optionId: IdType): boolean {
		const option = this.options.find((opt) => opt.id === optionId);
		return (
			option?.notCombinable ||
			optionId === this.defaultValue ||
			defaultFilterValues.includes(optionId)
		);
	}

	private handleMultiSelect(clickedId: IdType): void {
		const isNotCombinable = this.isNotCombinable(clickedId);

		if (isNotCombinable) {
			// if clicked option is not combinable (or is a default value), select only that option
			this._selected = [clickedId];
		} else {
			const itemWasSelected = this.selected.includes(clickedId);

			const hasNotCombinableSelected = this.selected.some((id) => this.isNotCombinable(id));

			if (hasNotCombinableSelected) {
				this._selected = [clickedId];
			} else if (itemWasSelected) {
				// item was selected, unselect it
				const newSelected = this.selected.filter((id) => id !== clickedId);
				// if this would result in empty selection, select the default value
				this._selected = newSelected.length > 0 ? newSelected : [this.defaultValue];
			} else {
				this._selected = [...this.selected, clickedId];
			}
		}
	}
}

// Helper type to accept any Filter type to work with FilterSort<any>
// This works around TypeScript's invariant generics
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyFilter<IdType extends FilterIdType = FilterIdType> = Filter<any, IdType>;
